/**
 * KnowledgeDashboard.jsx
 *
 * Right SidePanel page for viewing, searching, filtering, and curating
 * captured knowledge entries from the AI Cortex.
 *
 * Features:
 * - Paginated entry list with domain, date, prompt, type, feedback (8.2)
 * - Search and filter controls (8.3)
 * - Entity frequency ranked list (8.4)
 * - Export filtered entries as JSON (8.5)
 * - Admin curation: approve/reject entries (8.6)
 */

import {
    useState, useEffect, useCallback,
} from 'react';
import {
    Box, Typography, Button, TextField as MuiTextField,
    Select, MenuItem, Table, TableHead, TableBody,
    TableRow, TableCell, IconButton, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { App } from 'BMapsSrc/BMapsApp';
import { SidePanelContainer } from '../../info_display/SidePanel';
import { downloadData } from '../../ui_utils';

const KNOWLEDGE_ENDPOINT = '/services/cortex/knowledge/';
const DASHBOARD_TITLE = 'Knowledge Dashboard';

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.setOpen
 * @param {Function} props.handleClose
 */
export default function KnowledgeDashboard({
    open, setOpen, handleClose,
}) {
    // --- Data state ---
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [loading, setLoading] = useState(false);

    // --- Filter state (8.3) ---
    const [searchQuery, setSearchQuery] = useState('');
    const [domainFilter, setDomainFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // --- Entity frequency state (8.4) ---
    const [entityFreqs, setEntityFreqs] = useState([]);
    const [showEntities, setShowEntities] = useState(false);

    // --- Admin detection ---
    const isAdmin = (() => {
        const pm = App.getPermissionManager
            ? App.getPermissionManager() : null;
        return pm && (pm.isAllowed('admin')
            || pm.isAllowed('workflow_save'));
    })();

    // --- Fetch entries ---
    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
            });
            if (domainFilter) params.set('domain', domainFilter);
            if (dateFrom) params.set('dateFrom', dateFrom);
            if (dateTo) params.set('dateTo', dateTo);

            const response = await fetch(
                `${KNOWLEDGE_ENDPOINT}?${params}`,
                { credentials: 'include' }
            );
            if (!response.ok) {
                console.warn(
                    '[KnowledgeDashboard] Fetch failed:',
                    response.status
                );
                setLoading(false);
                return;
            }
            const data = await response.json();
            let filtered = data.entries || [];

            // Client-side search filter
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                filtered = filtered.filter(
                    (e) => (e.userPrompt || '').toLowerCase()
                        .includes(q)
                        || (e.resultSummary || '').toLowerCase()
                            .includes(q)
                );
            }

            setEntries(filtered);
            setTotal(data.total || 0);
        } catch (err) {
            console.warn(
                '[KnowledgeDashboard] Fetch error:', err.message
            );
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, domainFilter, dateFrom, dateTo, searchQuery]);

    // Fetch on mount and when filters change
    useEffect(() => {
        if (open) fetchEntries();
    }, [open, fetchEntries]);

    // --- Entity frequency (8.4) ---
    const fetchEntityFrequencies = useCallback(async () => {
        try {
            // Compute client-side from entries
            const freqMap = {};
            for (const e of entries) {
                const text = `${e.userPrompt || ''} ${
                    e.resultSummary || ''}`;
                // Simple word extraction for entities
                const words = text.match(
                    /\b[A-Z][A-Za-z0-9]{2,}\b/g
                ) || [];
                for (const w of words) {
                    freqMap[w] = (freqMap[w] || 0) + 1;
                }
            }
            const sorted = Object.entries(freqMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 20)
                .map(([entity, count]) => ({ entity, count }));
            setEntityFreqs(sorted);
        } catch (err) {
            console.warn(
                '[KnowledgeDashboard] Entity freq error:',
                err.message
            );
        }
    }, [entries]);

    useEffect(() => {
        if (showEntities && entries.length > 0) {
            fetchEntityFrequencies();
        }
    }, [showEntities, entries, fetchEntityFrequencies]);

    // --- Export (8.5) ---
    const handleExport = useCallback(() => {
        const json = JSON.stringify(entries, null, 2);
        downloadData(
            `cortex_knowledge_${new Date().toISOString()
                .slice(0, 10)}.json`,
            json,
            'application/json'
        );
    }, [entries]);

    // --- Admin curation (8.6) ---
    const handleCurate = useCallback(async (entryId, status) => {
        try {
            await fetch(`${KNOWLEDGE_ENDPOINT}${entryId}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    metadata: { curationStatus: status },
                }),
            });
            // Refresh after curation
            fetchEntries();
        } catch (err) {
            console.warn(
                '[KnowledgeDashboard] Curation error:',
                err.message
            );
        }
    }, [fetchEntries]);

    const totalPages = Math.ceil(total / pageSize) || 1;

    return (
        <SidePanelContainer
            title={DASHBOARD_TITLE}
            open={open}
            handleClose={handleClose}
            longLived
            width="28rem"
        >
            {/* 8.3 Search and filter controls */}
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <MuiTextField
                    size="small"
                    fullWidth
                    placeholder="Search entries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ mb: 1 }}
                    data-testid="knowledge-search"
                />
                <Box sx={{
                    display: 'flex', gap: 1, flexWrap: 'wrap',
                }}
                >
                    <Select
                        size="small"
                        value={domainFilter}
                        onChange={(e) => {
                            setDomainFilter(e.target.value);
                            setPage(1);
                        }}
                        displayEmpty
                        sx={{ minWidth: 100 }}
                        data-testid="knowledge-domain-filter"
                    >
                        <MenuItem value="">All Domains</MenuItem>
                        <MenuItem value="discovery">Discovery</MenuItem>
                        <MenuItem value="clinical">Clinical</MenuItem>
                        <MenuItem value="corporate">Corporate</MenuItem>
                    </Select>
                    <MuiTextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={dateFrom}
                        onChange={(e) => {
                            setDateFrom(e.target.value);
                            setPage(1);
                        }}
                        sx={{ width: 140 }}
                    />
                    <MuiTextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={dateTo}
                        onChange={(e) => {
                            setDateTo(e.target.value);
                            setPage(1);
                        }}
                        sx={{ width: 140 }}
                    />
                </Box>
                <Box sx={{
                    display: 'flex', gap: 1, mt: 1,
                }}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleExport}
                        data-testid="knowledge-export-btn"
                    >
                        Export as JSON
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowEntities(!showEntities)}
                        data-testid="knowledge-entities-btn"
                    >
                        {showEntities
                            ? 'Hide Entities'
                            : 'Entity Frequencies'}
                    </Button>
                </Box>
            </Box>

            {/* 8.4 Entity frequency view */}
            {showEntities && (
                <Box sx={{
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    maxHeight: 200,
                    overflow: 'auto',
                }}
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                    >
                        Most Referenced Entities
                    </Typography>
                    {entityFreqs.length === 0 ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            No entity data available.
                        </Typography>
                    ) : (
                        entityFreqs.map((ef) => (
                            <Box
                                key={ef.entity}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    py: 0.25,
                                }}
                            >
                                <Typography variant="body2">
                                    {ef.entity}
                                </Typography>
                                <Chip
                                    label={ef.count}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.7rem' }}
                                />
                            </Box>
                        ))
                    )}
                </Box>
            )}

            {/* 8.2 Knowledge entry list */}
            <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                {loading && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Loading...
                        </Typography>
                    </Box>
                )}
                {!loading && entries.length === 0 && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            No knowledge entries found.
                        </Typography>
                    </Box>
                )}
                {!loading && entries.length > 0 && (
                    <Table
                        size="small"
                        data-testid="knowledge-entry-table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Domain</TableCell>
                                <TableCell>Prompt</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Feedback</TableCell>
                                {isAdmin && (
                                    <TableCell>Actions</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map((entry) => (
                                <TableRow key={entry.id}>
                                    <TableCell sx={{
                                        fontSize: '0.75rem',
                                    }}
                                    >
                                        {new Date(entry.timestamp)
                                            .toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={entry.domain}
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontSize: '0.65rem',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        fontSize: '0.75rem',
                                        maxWidth: 150,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    >
                                        {(entry.userPrompt || '')
                                            .slice(0, 60)}
                                    </TableCell>
                                    <TableCell sx={{
                                        fontSize: '0.75rem',
                                    }}
                                    >
                                        {entry.responseType || '-'}
                                    </TableCell>
                                    <TableCell sx={{
                                        fontSize: '0.75rem',
                                    }}
                                    >
                                        {entry.feedback || '-'}
                                    </TableCell>
                                    {/* 8.6 Admin curation */}
                                    {isAdmin && (
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                title="Approve"
                                                onClick={() => handleCurate(
                                                    entry.id,
                                                    'approved'
                                                )}
                                                data-testid={
                                                    `approve-${entry.id}`
                                                }
                                            >
                                                <CheckCircleIcon
                                                    sx={{
                                                        fontSize: 16,
                                                    }}
                                                />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                title="Reject"
                                                onClick={() => handleCurate(
                                                    entry.id,
                                                    'rejected'
                                                )}
                                                data-testid={
                                                    `reject-${entry.id}`
                                                }
                                            >
                                                <CancelIcon
                                                    sx={{
                                                        fontSize: 16,
                                                    }}
                                                />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            {/* Pagination controls */}
            {total > pageSize && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
                >
                    <Button
                        size="small"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                    >
                        Prev
                    </Button>
                    <Typography variant="caption">
                        {page}
                        {' / '}
                        {totalPages}
                    </Typography>
                    <Button
                        size="small"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                    >
                        Next
                    </Button>
                </Box>
            )}
        </SidePanelContainer>
    );
}
